<template lang="pug">
.variable-pricing.p-XS
  div
    BriqTable.deals-table
      thead
        th.deal-heading Deal
        th.price-change-heading Price change
        th
      tbody
        template(v-for="deal in dealsSliced" v-if="deals")
          tr.deal-row(@click="toggleCollapse(deal)" data-test-id="toggle-configure-price-per-deal")
            td
              a(:href="editDealLink(deal)" target="_blank" rel="noopener noreferrer") {{ deal.InternalName }}
            td.text-center {{ priceChange(deal) }}
            td.slim-cell.text-center
              button.expand-button( :class="collapseIconClass(deal)")
                BriqIcon(name="icon-input-arrow-down-icon" :customStyle="{ fontSize: '8px' }")
          tr(v-show="isExpanded(deal)")
            td.deal-coulmn(colspan="3")
              DealProductsTable(
                forEditProduct
                :dealProducts="deal.Products"
                :editProductKey="product.Key"
                @dealProductPriceOffsetInput="handleDealProductPriceOffsetInput"
                )
    .deals-pagination
      BriqPagination(
        :total="totalDeals"
        @pageSelected="setPageSelected"
        @rowsPerTableSelected="setRowsPerTableSelected"
        )
</template>

<script>
import {
  contains,
  pipe,
  prop,
  map,
  sum,
  slice,
  chain,
  assoc,
  over,
  lensProp,
  propEq,
  reduce,
  isEmpty,
  dissoc,
} from 'ramda'
import { nonEmptyOr } from '@v/util/briqRamdaExt.js'
import InheritSwitch from '@v/settings/application/advanced/InheritSwitch.vue'
import DealProductsTable from '@v/components/DealProductsTable/DealProductsTable.vue'
import BriqPagination from '@v/components/BriqPagination.vue'
import { currencySymbol, currencyFormatter } from '@/helpers/intlFormatter.js'

export default {
  name: 'PriceConfigurationPerDeal',
  components: { InheritSwitch, DealProductsTable, BriqPagination },
  props: {
    product: { type: Object, required: true },
    disabled: Boolean,
    deals: { type: Array, required: true },
  },
  data() {
    return {
      expandedDeals: [],
      page: 1,
      rowsPerTable: 10,
    }
  },
  computed: {
    hasDeals() {
      return this.deals.length > 0
    },
    currencySymbol() {
      return currencySymbol(this.$locale, this.$currency)
    },
    priceSeparator() {
      return this.currencySymbol === '$' ? '' : ' '
    },
    dealsSliced() {
      return slice((this.page - 1) * this.rowsPerTable, this.page * this.rowsPerTable)(this.deals)
    },
    totalDeals() {
      return this.deals.length
    },
  },
  methods: {
    toggleCollapse(deal) {
      this.isExpanded(deal)
        ? (this.expandedDeals = this.expandedDeals.filter(key => key !== deal.Key))
        : this.expandedDeals.push(deal.Key)
    },
    isExpanded(deal) {
      return contains(deal.Key, this.expandedDeals)
    },
    priceChange(deal) {
      const roundNumber = number => Math.round(number * 100) / 100
      const summed = pipe(prop('Products'), map(prop('PriceOffset')), sum, roundNumber)(deal)
      return currencyFormatter(this.$locale, this.$currency).format(summed)
    },
    collapseIconClass(deal) {
      return this.isExpanded(deal) ? 'expanded' : 'collapsed'
    },
    editDealLink(deal) {
      return `/deals/${deal.Key}/edit`
    },
    setRowsPerTableSelected(val) {
      this.rowsPerTable = val
    },
    setPageSelected(val) {
      this.page = val
    },
    handleDealProductPriceOffsetInput({ dealProductKey, priceOffset }) {
      const mutation = assoc(dealProductKey, priceOffset || 0)
      const mutate = over(lensProp('DealChanges'), mutation)
      this.product.PriceConfiguration = mutate(this.product.PriceConfiguration)
    },
  },
  beforeMount() {
    const dealProductPriceConfiguration = getDealProductsPriceConfiguration(this.product)(
      this.deals,
    )
    if (!isEmpty(dealProductPriceConfiguration))
      this.product.PriceConfiguration = { DealChanges: dealProductPriceConfiguration }
  },
}

const getDealProductsPriceConfiguration = product => {
  const matchesProductKey = propEq('ProductKey', product.Key)
  return pipe(
    chain(prop('Products')),
    reduce((a, n) => (matchesProductKey(n) ? assoc(n.Key, n.PriceOffset, a) : a), {}),
  )
}
</script>

<style scoped lang="stylus">
@import '~@v/variables.styl'

.variable-pricing
    background-color #fff

.variable-pricing-header
  margin space-XS 0px space-XS 0px

.variable-pricing-description
  margin-bottom space-XS

.slim-cell
  padding 8px 2px

.expand-button
  padding 0 5px

a
  text-decoration underline

.deals-pagination
  display flex
  justify-content flex-end
  margin-top space-XS

.deals-table
  .deal-heading
    width 79.5%
  .price-change-heading
    max-width 20.5%

  .deal-row
    cursor pointer

  .deal-coulmn
    padding: space-XS
</style>

<style lang="stylus">
.variable-pricing
  .expand-button .icon
      transition transform 0.2s ease

  .expanded .icon
    transform rotate(180deg)
</style>
